import { cn } from '@/utils/classUtils';
import {
  Children,
  type FC,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const GAP_PX = 10; // matches gap-2.5 (2.5 * 4px)
const THRESHOLD_MIN_PX = 184;

type ChipFlexStyle = {
  flex: string;
  minWidth: number;
  maxWidth?: number;
};

function computeChipFlexStyles(
  rowWidth: number,
  intrinsicWidths: number[]
): ChipFlexStyle[] {
  const n = intrinsicWidths.length;
  if (n === 0) return [];
  if (rowWidth <= 0) {
    return intrinsicWidths.map(() => ({
      flex: '1 1 0%',
      minWidth: 0,
    }));
  }

  const gapTotal = GAP_PX * Math.max(0, n - 1);
  const threshold = Math.max(THRESHOLD_MIN_PX, rowWidth / 4);

  const longMask = intrinsicWidths.map(w => w > threshold);
  const shortSum = intrinsicWidths.reduce(
    (acc, w, i) => acc + (longMask[i] ? 0 : w),
    0
  );
  const longCount = longMask.filter(Boolean).length;
  const longIntrinsicSum = intrinsicWidths.reduce(
    (acc, w, i) => acc + (longMask[i] ? w : 0),
    0
  );

  let remaining = rowWidth - gapTotal - shortSum;

  // All chips fit at natural width
  if (remaining >= longIntrinsicSum) {
    return intrinsicWidths.map(() => ({
      flex: '0 0 auto',
      minWidth: 0,
    }));
  }

  // Long chips share horizontal space; short chips stay at intrinsic width
  if (remaining > 0 && longCount > 0) {
    const share = remaining / longCount;
    return intrinsicWidths.map((_w, i) =>
      longMask[i]
        ? {
            flex: '1 1 0%',
            minWidth: 0,
            maxWidth: share,
          }
        : { flex: '0 0 auto', minWidth: 0 }
    );
  }

  // Not enough room even for "short" chips: shrink everything equally
  return intrinsicWidths.map(() => ({
    flex: '1 1 0%',
    minWidth: 0,
  }));
}

function measureIntrinsicWidth(wrapper: HTMLDivElement): number {
  const saved = wrapper.style.cssText;
  wrapper.style.flex = '0 0 auto';
  wrapper.style.width = 'max-content';
  wrapper.style.maxWidth = 'none';
  const w = wrapper.getBoundingClientRect().width;
  wrapper.style.cssText = saved;
  return w;
}

export const NewsfeedRelevantChipsRow: FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [styles, setStyles] = useState<ChipFlexStyle[]>([]);

  const childArray = Children.toArray(children);
  const count = childArray.length;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || count === 0) {
      setStyles([]);
      return;
    }

    const run = () => {
      const rowWidth = container.clientWidth;
      const widths: number[] = [];
      for (let i = 0; i < count; i++) {
        const el = itemRefs.current[i];
        widths.push(el ? measureIntrinsicWidth(el) : 0);
      }
      setStyles(computeChipFlexStyles(rowWidth, widths));
    };

    run();

    const ro = new ResizeObserver(run);
    ro.observe(container);
    return () => ro.disconnect();
  }, [count]);

  itemRefs.current.length = 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex flex-nowrap items-center gap-2.5 min-w-0 w-full',
        className
      )}
    >
      {childArray.map((child, i) => (
        <div
          key={i}
          ref={el => {
            itemRefs.current[i] = el;
          }}
          className="min-w-0 flex justify-start [&>*]:min-w-0 [&>*]:max-w-full"
          style={
            styles[i]
              ? {
                  flex: styles[i].flex,
                  minWidth: styles[i].minWidth,
                  maxWidth: styles[i].maxWidth,
                }
              : { flex: '0 0 auto', minWidth: 0 }
          }
        >
          {child}
        </div>
      ))}
    </div>
  );
};
